import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { KeyRound, Loader2, ShieldAlert } from 'lucide-react';
import { clsx } from 'clsx';
import {
  deleteByokKey,
  fetchByokAudit,
  fetchByokStatus,
  setByokEnabled,
  saveByokKey,
  testByokConnection,
  type ByokAuditEntry,
  type ByokStatus,
} from '@/api/aiByok';
import { ByokProviderCard } from '@/components/settings/ByokProviderCard';
import {
  BYOK_PROVIDER_CATALOG,
  DEFAULT_OPENAI_CHAT_MODEL,
  type ByokProviderId,
} from '@/components/settings/byok-provider-catalog';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

type ProviderDraft = {
  apiKey: string;
  endpoint: string;
  model: string;
};

function formatAuditTime(entry: ByokAuditEntry): string {
  const at = entry.at;
  if (typeof at === 'number') return new Date(at).toLocaleString();
  if (at && typeof at === 'object' && typeof at._seconds === 'number') {
    return new Date(at._seconds * 1000).toLocaleString();
  }
  return '—';
}

function emptyDraft(defaultBaseUrl: string, model = DEFAULT_OPENAI_CHAT_MODEL): ProviderDraft {
  return { apiKey: '', endpoint: defaultBaseUrl, model };
}

export const AiByokSettings = ({ embedded = false }: { embedded?: boolean }) => {
  const { t } = useTranslation('settings');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ByokStatus | null>(null);
  const [audit, setAudit] = useState<ByokAuditEntry[]>([]);
  const [expandedId, setExpandedId] = useState<ByokProviderId | null>('openai');
  const [drafts, setDrafts] = useState<Record<ByokProviderId, ProviderDraft>>(() =>
    Object.fromEntries(
      BYOK_PROVIDER_CATALOG.map((p) => [p.id, emptyDraft(p.defaultBaseUrl)])
    ) as Record<ByokProviderId, ProviderDraft>
  );
  const [busyProvider, setBusyProvider] = useState<ByokProviderId | null>(null);
  const [busyAction, setBusyAction] = useState<'test' | 'save' | 'delete' | 'toggle' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; embed?: boolean; chat?: boolean } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const activeProviderId = useMemo((): ByokProviderId | null => {
    if (status?.provider === 'openai') return 'openai';
    return null;
  }, [status?.provider]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [st, items] = await Promise.all([fetchByokStatus(), fetchByokAudit(5)]);
      setStatus(st);
      setAudit(items);
      if (st.provider === 'openai') {
        setExpandedId('openai');
        setDrafts((prev) => ({
          ...prev,
          openai: {
            ...prev.openai,
            endpoint: st.endpoint?.trim() || prev.openai.endpoint,
            model: st.chatModel?.trim() || prev.openai.model,
          },
        }));
      }
    } catch {
      setStatus(null);
      setAudit([]);
      setError(t('aiByok.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const showProviders =
    status?.canConfigureByok && status?.isAccountAdmin && status?.byokServerEnabled && status?.planInRollout;
  const readOnlyMember = status?.canConfigureByok && !status?.isAccountAdmin;

  const patchDraft = (id: ByokProviderId, patch: Partial<ProviderDraft>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const handleTest = async (providerId: ByokProviderId) => {
    const provider = BYOK_PROVIDER_CATALOG.find((p) => p.id === providerId);
    if (!provider?.backendProvider) return;
    const draft = drafts[providerId];
    if (!draft.apiKey.trim()) return;
    setBusyProvider(providerId);
    setBusyAction('test');
    setError(null);
    setTestResult(null);
    try {
      const res = await testByokConnection({
        apiKey: draft.apiKey.trim(),
        provider: provider.backendProvider,
        endpoint: draft.endpoint.trim() || provider.defaultBaseUrl,
        chatModel: draft.model,
      });
      setTestResult({ ok: res.ok, embed: res.checks?.embed, chat: res.checks?.chat });
      if (!res.ok) setError(t('aiByok.testFailed'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('aiByok.testFailed'));
    } finally {
      setBusyProvider(null);
      setBusyAction(null);
    }
  };

  const handleSave = async (providerId: ByokProviderId) => {
    const provider = BYOK_PROVIDER_CATALOG.find((p) => p.id === providerId);
    if (!provider?.backendProvider) return;
    const draft = drafts[providerId];
    if (!draft.apiKey.trim()) return;
    setBusyProvider(providerId);
    setBusyAction('save');
    setError(null);
    try {
      await saveByokKey({
        apiKey: draft.apiKey.trim(),
        provider: provider.backendProvider,
        endpoint: draft.endpoint.trim() || provider.defaultBaseUrl,
        chatModel: draft.model,
        enabled: true,
      });
      patchDraft(providerId, { apiKey: '' });
      setTestResult(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('aiByok.saveFailed'));
    } finally {
      setBusyProvider(null);
      setBusyAction(null);
    }
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    setBusyProvider('openai');
    setBusyAction('toggle');
    setError(null);
    try {
      await setByokEnabled(enabled);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('aiByok.toggleFailed'));
    } finally {
      setBusyProvider(null);
      setBusyAction(null);
    }
  };

  const handleDelete = async () => {
    setBusyProvider('openai');
    setBusyAction('delete');
    setError(null);
    try {
      await deleteByokKey();
      setConfirmDelete(false);
      patchDraft('openai', { apiKey: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('aiByok.deleteFailed'));
    } finally {
      setBusyProvider(null);
      setBusyAction(null);
    }
  };

  return (
    <div
      id={embedded ? 'byok' : undefined}
      className={embedded ? 'space-y-6' : 'mx-auto max-w-3xl space-y-8 px-4 py-8 md:px-8'}
    >
      {!embedded ? (
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-foreground">
            <KeyRound className="h-7 w-7 text-primary" aria-hidden />
            {t('aiByok.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('aiByok.subtitle')}</p>
        </div>
      ) : (
        <div>
          <h3 className="text-base font-bold text-foreground">{t('aiByok.title')}</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t('aiByok.subtitle')}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('aiByok.loading')}
        </div>
      ) : (
        <div className="space-y-6">
          {!status?.byokServerEnabled ? (
            <div className="rounded-2xl border border-border bg-muted/30 p-5 text-sm text-muted-foreground">
              <p>{t('aiByok.serverOff')}</p>
              <p className="mt-2 font-mono text-[11px]">{t('aiByok.runbookPath')}</p>
            </div>
          ) : null}

          {status?.byokServerEnabled && !status?.planAllowsByok ? (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 text-sm">
              <p>{t('aiByok.upgradeRequired')}</p>
              <Link to="/settings/subscription" className="mt-2 inline-block text-sm font-semibold text-primary">
                {t('aiByok.upgradeCta')}
              </Link>
            </div>
          ) : null}

          {status?.planAllowsByok && !status?.planInRollout ? (
            <div className="rounded-2xl border border-border bg-muted/30 p-5 text-sm text-muted-foreground">
              {t('aiByok.rolloutPending')}
            </div>
          ) : null}

          {readOnlyMember ? (
            <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
              {t('aiByok.memberReadOnly')}
            </div>
          ) : null}

          <div className="rounded-2xl border border-border bg-card p-5 text-sm space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-foreground">{t('aiByok.billingMode')}:</span>
              <span
                className={clsx(
                  'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  status?.mode === 'byok' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                )}
              >
                {status?.mode === 'byok' ? t('aiByok.modeByok') : t('aiByok.modeHosted')}
              </span>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">{t('aiByok.privacyBody')}</p>
            {status?.disabledReason ? (
              <p className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {t('aiByok.disabledReason', { reason: status.disabledReason })}
              </p>
            ) : null}
          </div>

          {showProviders ? (
            <div className="space-y-3">
              {BYOK_PROVIDER_CATALOG.map((provider) => {
                const isActive = activeProviderId === provider.id;
                const draft = drafts[provider.id];
                const cardBusy = busyProvider === provider.id ? busyAction : null;
                return (
                  <ByokProviderCard
                    key={provider.id}
                    provider={provider}
                    expanded={expandedId === provider.id}
                    onToggleExpand={() =>
                      setExpandedId((prev) => (prev === provider.id ? null : provider.id))
                    }
                    active={isActive && Boolean(status?.keyLast4)}
                    hasSavedKey={isActive && Boolean(status?.keyLast4)}
                    enabled={isActive && status?.enabled === true}
                    toggleBusy={cardBusy === 'toggle'}
                    onToggleEnabled={(next) => void handleToggleEnabled(next)}
                    canEdit={provider.available}
                    apiKey={draft.apiKey}
                    onApiKeyChange={(value) => patchDraft(provider.id, { apiKey: value })}
                    endpoint={draft.endpoint}
                    onEndpointChange={(value) => patchDraft(provider.id, { endpoint: value })}
                    selectedModel={draft.model}
                    onModelChange={(model) => patchDraft(provider.id, { model })}
                    keyLast4={isActive ? status?.keyLast4 : null}
                    busy={cardBusy}
                    error={expandedId === provider.id ? error : null}
                    testResult={expandedId === provider.id ? testResult : null}
                    onTest={() => void handleTest(provider.id)}
                    onSave={() => void handleSave(provider.id)}
                    onDelete={() => setConfirmDelete(true)}
                  />
                );
              })}
            </div>
          ) : null}

          {audit.length > 0 ? (
            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="text-sm font-semibold text-foreground">{t('aiByok.auditTitle')}</h2>
              <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
                {audit.map((entry, i) => (
                  <li key={`${entry.action}-${i}`} className="flex justify-between gap-4 border-b border-border-muted pb-2 last:border-0">
                    <span>
                      {entry.action}
                      {entry.keyLast4 ? ` · ${entry.keyLast4}` : ''}
                    </span>
                    <span className="shrink-0">{formatAuditTime(entry)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t('aiByok.deleteConfirmTitle')}
        confirmLabel={t('aiByok.deleteKey')}
        isConfirming={busyAction === 'delete'}
        onConfirm={() => void handleDelete()}
      >
        <p className="text-sm text-muted-foreground">{t('aiByok.deleteConfirmBody')}</p>
      </ConfirmModal>
    </div>
  );
};
